<template>
  <span
    class="die3d-scene"
    :class="{ 'die3d-scene--dim': dim && !phase, 'die3d-scene--placeholder': placeholder }"
  >
    <!--
      Two nested elements because two different motions compose:
      the MOVER owns where the die is (the shake jitter, the gather toward
      the middle of the tray, the hop and landing of the throw), the CUBE
      owns which way it faces (the tumble to the final value). Keeping them
      separate is what lets the rotation ease out on its own long curve
      while the bounce keyframes do something completely different.
    -->
    <span class="die3d-mover" :class="moverClass" :style="moverStyle">
      <span class="die3d" :class="{ 'die3d--snap': snap }" :style="cubeStyle">
        <span v-for="(side, i) in SIDES" :key="i" class="die3d__face" :style="side.style">
          <i
            v-for="n in 9"
            :key="n"
            class="die3d__pip"
            :class="{ 'die3d__pip--on': !placeholder && side.cells.includes(n) }"
          />
        </span>
      </span>
    </span>
  </span>
</template>

<script>
// A die that actually rolls. Second iteration, both shaped by Matt's own
// notes (2026-08-29). First: "can we animate cubes and roll real dice?" -
// hence a real cube in CSS 3D. Then, on v1: "the tumbling piece is good.
// The shake-up-together looks like a glitch... they should tumble for
// longer, and each for their own amount of time." So the huddle-and-rattle
// opening is GONE, and what remains is the part that worked, deepened:
//
//   'tumble' - the cube springs from a random diagonal and spins several
//              full turns on both axes, bobbling as it goes, easing out to
//              land on its value. Every die gets its OWN duration (the
//              parent deals them), its own spin count scaled to that
//              duration, and its own directions - four dice, four different
//              landing moments, no lockstep anywhere.
//   null     - at rest, showing `value`.
//
// The cube's final orientation IS the rolled value - the last half-turn of
// the tumble is the reveal, not a swap. The long ease-out is most of what
// makes it read as a thrown object rather than a spinner. All movement is
// transform-only, so the die never changes its layout footprint (the
// tray-stability report).
//
// PORTABLE ON PURPOSE. Matt: "there's other games where we would like to
// have the same dice animation." Nothing here imports from this app - no
// store, no engine, no game rules. Copy the file plus the .die3d* styles,
// pass `phase: 'tumble'` with a per-die `duration`, drop phase back to null
// when your clock says the longest die has landed. (Space Base is the
// obvious next customer - its whole turn starts with a dice roll.)

// The cube's six sides, in opposite pairs: front/back, right/left,
// top/bottom. Each side is authored flat, pushed out to its place, and
// carries the rotation that brings it square to the viewer.
const SIDES = [
  { transform: '', rot: { x: 0, y: 0 } },
  { transform: 'rotateY(180deg)', rot: { x: 0, y: 180 } },
  { transform: 'rotateY(90deg)', rot: { x: 0, y: -90 } },
  { transform: 'rotateY(-90deg)', rot: { x: 0, y: 90 } },
  { transform: 'rotateX(90deg)', rot: { x: -90, y: 0 } },
  { transform: 'rotateX(-90deg)', rot: { x: 90, y: 0 } },
];

const D6 = [1, 2, 3, 4, 5, 6];

/**
 * Lay a die's values onto those six sides. Opposite sides take values from
 * opposite ends of the list, which is what makes a western d6 read 1
 * against 6, 2 against 5, 3 against 4 - and a list SHORTER than six simply
 * goes round again, the way a three-sided die is really a cube with every
 * number on it twice.
 *
 * Matt, 2026-09-19: "these are D3s and it's showing fives as it rolls
 * along. We should make it only show the faces that actually would exist
 * on that relevant die."
 */
const arrange = (faces) => {
  const list = faces && faces.length ? faces : D6;
  const out = [];
  for (let i = 0; i < 3; i += 1) {
    const near = i % list.length;
    out.push(list[near], list[(list.length - 1 - near) % list.length]);
  }
  return out;
};

const PIP_CELLS = {
  1: [5],
  2: [3, 7],
  3: [3, 5, 7],
  4: [1, 3, 7, 9],
  5: [1, 3, 5, 7, 9],
  6: [1, 3, 4, 6, 7, 9],
};
// Above six there are no pips anybody could count, so the die shows the
// middle three cells - a bar - rather than lying about what it rolled.
// A zero is a BLANK face: the bar read as three dots on a 0-2 die
// (commonwealth playtest -VJ1n, "it showed a three and the text said zero").
const cellsFor = (value) => (value === 0 ? [] : PIP_CELLS[value] || [4, 5, 6]);

export default {
  name: 'Die3d',
  props: {
    value: { type: Number, default: 1 },
    // Which values this die can show, low to high. A d6 by default, so
    // every caller that has one need say nothing; pass [1, 2, 3] for a d3,
    // or [2, 3, 4] for a d3 that starts at two.
    faces: { type: Array, default: () => D6 },
    // 'tumble' | null - the parent runs the clock; this component only ever
    // renders the phase it is told.
    phase: { type: String, default: null },
    // This die's own tumble length, in ms - dealt by the parent so the
    // parent also knows when the LAST die lands. Spin count scales with it,
    // so a long tumble is more turns, not slower ones.
    duration: { type: Number, default: 1600 },
    dim: { type: Boolean, default: false },
    placeholder: { type: Boolean, default: false },
  },
  data() {
    return {
      // The rotation the cube is currently heading to (or holding).
      rot: { ...this.showRotation(this.value) },
      // Snap = apply orientation with NO transition (rest state, reduced
      // motion, and the jump to the launch pose a tumble springs from).
      snap: true,
    };
  },
  computed: {
    // The six sides as drawn: where each sits, and what is printed on it.
    SIDES() {
      const values = arrange(this.faces);
      return SIDES.map((side, i) => ({
        style: { transform: `${side.transform} translateZ(var(--die3d-half))` },
        cells: cellsFor(values[i]),
      }));
    },
    moverClass() {
      return this.phase === 'tumble' ? 'die3d-mover--tumble' : null;
    },
    moverStyle() {
      // The bobble runs exactly as long as this die's own tumble.
      return { '--die3d-duration': `${this.duration}ms` };
    },
    cubeStyle() {
      return {
        transform: `rotateX(${this.rot.x}deg) rotateY(${this.rot.y}deg)`,
        transitionDuration: `${this.duration}ms`,
      };
    },
  },
  methods: {
    /** The cube rotation that brings this value's side square to the viewer. */
    showRotation(value) {
      const values = arrange(this.faces);
      const i = values.indexOf(value);
      return { ...SIDES[i < 0 ? 0 : i].rot };
    },
  },
  watch: {
    phase(next) {
      if (next === 'tumble') {
        // Spring from a fresh random diagonal, then tumble to the value.
        // Two writes with a forced reflow between them, so the launch pose
        // commits WITHOUT a transition and the spin then animates from it -
        // rAF would do the same job but is paused in occluded windows,
        // which is exactly where the automated tests watch from.
        this.snap = true;
        this.rot = {
          x: 20 + Math.round(Math.random() * 50),
          y: 20 + Math.round(Math.random() * 50),
        };
        // eslint-disable-next-line no-unused-expressions
        this.$el?.offsetHeight;
        this.$nextTick(() => {
          // eslint-disable-next-line no-unused-expressions
          this.$el?.offsetHeight;
          const target = this.showRotation(this.value);
          // Spin count scales with this die's duration (~one full turn per
          // 450ms), directions drawn per axis - a long die is MORE turns at
          // the same felt speed, not the same turns slower. Math.random is
          // fine here and forbidden in the engine: this is theatre, and the
          // value was decided the moment the action dispatched.
          const turns = Math.max(3, Math.round(this.duration / 450));
          const spins = () => turns * 360 * (Math.random() < 0.5 ? -1 : 1);
          this.snap = false;
          this.rot = { x: target.x + spins(), y: target.y + spins() };
        });
      } else {
        // At rest: normalize to the plain orientation so the next tumble's
        // spin count means what it says.
        this.snap = true;
        this.rot = { ...this.showRotation(this.value) };
      }
    },
    value() {
      if (!this.phase) this.rot = { ...this.showRotation(this.value) };
    },
  },
};
</script>
